import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { Link } from 'react-router';
import { FlaskConical, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useRunCriticalPlan, useTestingCommandCenter } from './testing.hooks';

export function TestingCommandCenterPage() {
  const { permissions } = useAuth();
  const { formatDateTime: formatDate } = useUserPreferences();
  const dashboardQuery = useTestingCommandCenter();
  const runMutation = useRunCriticalPlan();
  const canAdmin = hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN);
  const runError = runMutation.error instanceof Error ? runMutation.error.message : null;

  if (!canAdmin) {
    return (
      <EmptyState
        icon={<FlaskConical className="h-5 w-5" aria-hidden="true" />}
        title="Testing automation access required"
        description={`Your role needs ${PERMISSIONS.PLATFORM_ADMIN} to view the testing catalog.`}
      />
    );
  }

  if (dashboardQuery.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ErrorState
        title="Unable to load testing automation"
        description={
          dashboardQuery.error instanceof Error
            ? dashboardQuery.error.message
            : 'Unknown testing automation error'
        }
        action={
          <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const data = dashboardQuery.data;

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow="S33 · Testing Automation"
          title="Testing Automation"
          description="Check packaged test coverage across critical journeys. These checks verify test files; the release pipeline executes the test suites."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Refresh
              </Button>
              <Button
                size="sm"
                disabled={runMutation.isPending || !data.permissions.canRunTesting}
                onClick={() => {
                  runMutation.reset();
                  runMutation.mutate();
                }}
              >
                <FlaskConical className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {runMutation.isPending ? 'Checking…' : 'Check test coverage'}
              </Button>
            </div>
          }
        />
      }
      toolbar={
        <>
          {runError ? <p className="text-destructive text-sm">{runError}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Journeys</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.journeyCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Critical</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.criticalCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Suite kinds</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.suiteKindCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Test files present
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.latestPassRate == null ? '—' : `${data.summary.latestPassRate}%`}
              </p>
            </Card>
          </div>
        </>
      }
      sections={[
        {
          id: 'critical-journeys',
          label: 'Critical journeys',
          content: (
            <Card className="p-3">
              <h2 className="hx-section-title mb-3 text-sm font-semibold">Critical journeys</h2>
              <ul className="space-y-2">
                {data.journeys.map((journey) => (
                  <li
                    key={journey.id}
                    className="border-border flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium">{journey.name}</p>
                      <p className="text-muted-foreground text-xs">{journey.description}</p>
                      <p className="text-muted-foreground mt-1 text-xs">{journey.specPath}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{journey.suiteKind}</Badge>
                      <Badge variant={journey.specExists ? 'success' : 'critical'}>
                        {journey.specExists ? 'Spec present' : 'Spec missing'}
                      </Badge>
                      {journey.routePath ? (
                        <Button size="sm" variant="outline" asChild>
                          <Link to={journey.routePath}>Open</Link>
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ),
        },
        {
          id: 'coverage-check-history',
          label: 'Coverage check history',
          content: (
            <Card className="p-3">
              <h2 className="hx-section-title mb-3 text-sm font-semibold">
                Coverage check history
              </h2>
              {data.runs.length === 0 ? (
                <EmptyState
                  icon={<FlaskConical className="h-5 w-5" aria-hidden="true" />}
                  title="No coverage checks yet"
                  description="Check test coverage to verify registered test files are included in this build."
                />
              ) : (
                <ul className="space-y-2">
                  {data.runs.map((run) => (
                    <li key={run.id} className="border-border rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{run.runNumber}</span>
                        <Badge variant="outline">{run.status}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {run.summary} · {formatDate(run.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
      ]}
    />
  );
}
